import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { useArchiveFeedback } from '~/features/list-archive/hooks/use-archive-feedback';
import type { ArchiveBatchResponse } from '~/features/list-archive/types/list-archive.types';

import { setEnrichmentJobsArchivedBatch } from '../services/catalog-operations.service';

interface BatchArgs {
  ids: string[];
  archived: boolean;
}

/**
 * Bulk archive / unarchive for enrichment-job rows (Catalog Operations + Feed
 * Audit). The server answers 200 whenever the request is well-formed, so the
 * outcome lives in `summary` / `results`, not the HTTP status: one summary
 * toast is shown and the whole catalog-operations subtree is invalidated —
 * archived rows leave on refetch, refused ones stay (and stay selected).
 */
export function useSetEnrichmentJobsArchivedBatch() {
  const queryClient = useQueryClient();
  const { notifyBatch, notifyError } = useArchiveFeedback();
  const mutation = useMutation<ArchiveBatchResponse, unknown, BatchArgs>({
    mutationFn: ({ ids, archived }) => setEnrichmentJobsArchivedBatch(ids, archived),
    onSuccess: (response, { archived }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all });
      notifyBatch(response.summary, archived);
    },
    onError: (err) => {
      // Only a malformed request (empty ids, over the cap) or a tenant-less
      // token reaches here — per-item refusals are inside the 200.
      notifyError(err);
    },
  });
  return {
    run: (ids: string[], archived: boolean) => {
      mutation.mutate({ ids, archived });
    },
    isPending: mutation.isPending,
  };
}
